import React from 'react';
import { TrendingUp, Tags, Bell, Smartphone, Clock, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { purpleColors, violetColors, slateColors, Section, Container, SectionTitle, TitleHighlight, SectionDescription } from '../styles';

const featureMeta = [
  { icon: TrendingUp, color: 'blue' as const },
  { icon: Tags, color: 'cyan' as const },
  { icon: Bell, color: 'blue' as const },
  { icon: Smartphone, color: 'cyan' as const },
  { icon: Clock, color: 'blue' as const },
  { icon: MapPin, color: 'cyan' as const },
];

type FeaturesProps = {
  part?: 'intro' | 'more' | 'all';
};

const getLocalizedStationHighlightScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Stationhighlight';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Stationhighlight_DE.jpeg`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Stationhighlight_FR.jpeg`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Stationhighlight_NL.jpeg`;
  }
  return `${base}/Stationhighlight_EN.jpeg`;
};

const getLocalizedDepartureSectionScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Departuresection';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Departuresection_DE.png`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Departuresection_FR.png`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Departuresection_NL.png`;
  }
  return `${base}/Departuresection_EN.png`;
};

const comparisonTagIds = ['direct', 'fastest', 'cheapest', 'mostreliable', 'leastbusy'] as const;

const getComparisonTagSvgPath = (language: string, tag: (typeof comparisonTagIds)[number]): string => {
  const base = '/images/Applandingpage/Comparisontags';
  const normalized = language.toLowerCase();
  let suffix: 'DE' | 'EN' | 'FR' | 'NL' = 'EN';
  if (normalized.startsWith('de')) {
    suffix = 'DE';
  } else if (normalized.startsWith('fr')) {
    suffix = 'FR';
  } else if (normalized.startsWith('nl')) {
    suffix = 'NL';
  }
  return `${base}/${tag}_${suffix}.svg`;
};

const getLocalizedNotificationImagePath = (language: string): string => {
  const base = '/images/Applandingpage/Notifications';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Notification_DE.png`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Notification_FR.png`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Notification_NL.png`;
  }
  return `${base}/Notification_EN.png`;
};

const getLocalizedLiveActivityCroppedImagePath = (language: string): string => {
  const base = '/images/Applandingpage/Live%20Activity/cropped';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Liveactivity_DE.png`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Liveactivity_FR.png`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Liveactivity_NL.png`;
  }
  return `${base}/Liveactivity_EN.png`;
};

const getLocalizedReliabilityScoreImagePath = (language: string): string => {
  const base = '/images/Applandingpage/Reliabilityscore';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Reliabilityscore_DE.png`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Reliabilityscore_FR.png`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Reliabilityscore_NL.png`;
  }
  return `${base}/Reliabilityscore_EN.png`;
};

const FeaturesSection = styled(Section)`
  background: linear-gradient(to bottom, ${purpleColors[50]}, ${violetColors[500]}15, ${purpleColors[50]});
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 5rem;
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 3rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    padding: 3rem;
  }
`;

const FeatureCard = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-radius: 1rem;
  background: linear-gradient(to bottom, ${purpleColors[50]}, ${violetColors[500]}15, ${purpleColors[50]});
  border: 1px solid ${purpleColors[200]};
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    border-color: ${purpleColors[300]};
  }
`;

const IconWrapper = styled.div<{ color: 'blue' | 'cyan' }>`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.75rem;
  background: linear-gradient(to bottom right, 
    ${props => props.color === 'blue' ? purpleColors[500] : violetColors[500]}, 
    ${props => props.color === 'blue' ? purpleColors[600] : violetColors[600]});
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  transition: transform 0.2s;
  
  ${FeatureCard}:hover & {
    transform: scale(1.1);
  }
  
  svg {
    width: 1.75rem;
    height: 1.75rem;
    color: white;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin-bottom: 0.75rem;
`;

const CardDescription = styled.p`
  color: ${slateColors[600]};
  line-height: 1.6;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: center;
  margin-top: 6rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }
`;

const ContentColumn = styled.div<{ order?: number }>`
  order: 2;
  
  @media (min-width: 1024px) {
    order: ${props => props.order || 1};
  }
`;

const ImageColumn = styled.div<{ order?: number }>`
  order: 1;
  position: relative;
  
  @media (min-width: 1024px) {
    order: ${props => props.order || 1};
  }
  
  img {
    width: 100%;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }
`;

const NotificationImageColumn = styled(ImageColumn)`
  img {
    box-shadow: none;
  }
`;

const SubHeading = styled.h3`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1.5rem;
`;

const SubDescription = styled.p`
  font-size: 1.125rem;
  color: ${slateColors[600]};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BulletDot = styled.div<{ color?: string }>`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  background: ${props => {
    if (props.color === 'blue') return '#dbeafe';
    if (props.color === 'yellow') return '#fef3c7';
    if (props.color === 'violet') return `${violetColors[500]}20`;
    if (props.color === 'green') return '#d1fae5';
    if (props.color === 'orange') return '#fed7aa';
    if (props.color === 'purple') return `${purpleColors[200]}`;
    return `${purpleColors[100]}`;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &::after {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 9999px;
    background: ${props => {
      if (props.color === 'blue') return '#2563eb';
      if (props.color === 'yellow') return '#d97706';
      if (props.color === 'violet') return violetColors[600];
      if (props.color === 'green') return '#059669';
      if (props.color === 'orange') return '#ea580c';
      if (props.color === 'purple') return purpleColors[600];
      return purpleColors[600];
    }};
  }
`;

const BulletText = styled.span`
  color: ${slateColors[700]};
`;

export default function Features({ part = 'all' }: FeaturesProps) {
  const { t, i18n } = useTranslation();
  const showIntro = part === 'intro' || part === 'all';
  const showMore = part === 'more' || part === 'all';

  return (
    <FeaturesSection id="features">
      <Container>
        {showIntro && (
          <>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <SectionTitle>
            {t('appLandingPage.features.title.regular')}
            <TitleHighlight>{t('appLandingPage.features.title.highlight')}</TitleHighlight>
          </SectionTitle>
          <SectionDescription>{t('appLandingPage.features.description')}</SectionDescription>
        </div>

{/*         <CardsGrid>
          {featureMeta.map(({ icon: Icon, color }, index) => {
            return (
              <FeatureCard key={index}>
                <IconWrapper color={color}>
                  <Icon />
                </IconWrapper>
                <CardTitle>{t(`appLandingPage.features.cards.${index}.title`)}</CardTitle>
                <CardDescription>{t(`appLandingPage.features.cards.${index}.description`)}</CardDescription>
              </FeatureCard>
            );
          })}
        </CardsGrid> */}

        <TwoColumnGrid>
          <ContentColumn>
            <SubHeading>{t('appLandingPage.features.notifications.heading')}</SubHeading>
            <SubDescription>{t('appLandingPage.features.notifications.description')}</SubDescription>
            <BulletList>
              {[0, 1, 2].map((index) => (
                <BulletItem key={index}>
                  <BulletDot />
                  <BulletText>{t(`appLandingPage.features.notifications.bullets.${index}`)}</BulletText>
                </BulletItem>
              ))}
            </BulletList>
          </ContentColumn>
          <NotificationImageColumn>
            <img
              src={getLocalizedNotificationImagePath(i18n.language)}
              alt={t('appLandingPage.features.notifications.imageAlt')}
            />
          </NotificationImageColumn>
        </TwoColumnGrid>

        <TwoColumnGrid>
         <ImageColumn order={1}>
            <img
              src={getLocalizedDepartureSectionScreenshot(i18n.language)}
              alt={t('appLandingPage.features.liveBoarding.imageAlt')}
              style={{ borderRadius: '1.5rem' }}
            />
          </ImageColumn>
          <ContentColumn order={1}>
            <SubHeading>{t('appLandingPage.features.liveBoarding.heading')}</SubHeading>
            <SubDescription>{t('appLandingPage.features.liveBoarding.description')}</SubDescription>
            <BulletList>
              {[0, 1, 2].map((index) => (
                <BulletItem key={index}>
                  <BulletDot color="blue" />
                  <BulletText>{t(`appLandingPage.features.liveBoarding.bullets.${index}`)}</BulletText>
                </BulletItem>
              ))}
            </BulletList>
          </ContentColumn>
        </TwoColumnGrid>

        <TwoColumnGrid>
          <ContentColumn order={2}>
                <SubHeading>{t('appLandingPage.features.smartTags.heading')}</SubHeading>
                <SubDescription>{t('appLandingPage.features.smartTags.description')}</SubDescription>
            <BulletList>
                  {[0, 1, 2].map((index) => (
                <BulletItem key={index}>
                  <BulletDot color="orange" />
                  <BulletText>{t(`appLandingPage.features.smartTags.bullets.${index}`)}</BulletText>
                </BulletItem>
              ))}
            </BulletList>
          </ContentColumn>
          <ImageColumn order={2}>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignContent: 'flex-start',
              }}
            >
              {comparisonTagIds.map((tagId) => (
                <img
                  key={tagId}
                  src={getComparisonTagSvgPath(i18n.language, tagId)}
                  alt={t(`appLandingPage.features.smartTags.tagAlts.${tagId}`)}
                  style={{
                    height: '3rem',
                    width: 'auto',
                    borderRadius: '9999px',
                    objectFit: 'contain',
                  }}
                />
              ))}
            </div>
          </ImageColumn>
        </TwoColumnGrid>

        <TwoColumnGrid>
          <ImageColumn order={1}>
            <img
              src={getLocalizedStationHighlightScreenshot(i18n.language)}
              alt={t('appLandingPage.features.stationHighlights.imageAlt')}
              style={{ borderRadius: '1.0rem' }}
            />
          </ImageColumn>
          <ContentColumn order={2}>
                <SubHeading>{t('appLandingPage.features.stationHighlights.heading')}</SubHeading>
                <SubDescription>{t('appLandingPage.features.stationHighlights.description')}</SubDescription>
            <BulletList>
                  {[0, 1, 2].map((index) => (
                <BulletItem key={index}>
                  <BulletDot color="green" />
                  <BulletText>{t(`appLandingPage.features.stationHighlights.bullets.${index}`)}</BulletText>
                </BulletItem>
              ))}
            </BulletList>
          </ContentColumn>
        </TwoColumnGrid>


          </>
        )}

        {showMore && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <SectionTitle>{t('appLandingPage.features.moreFeaturesTitle')}</SectionTitle>
              <SectionDescription>{t('appLandingPage.features.moreFeaturesSubtitle')}</SectionDescription>
            </div>

        <TwoColumnGrid>
          <ImageColumn order={2}>
            <img
              src={getLocalizedLiveActivityCroppedImagePath(i18n.language)}
              alt={t('appLandingPage.features.liveActivities.imageAlt')}
              style={{ borderRadius: '2.5rem' }}
            />
          </ImageColumn>
          <ContentColumn order={1}>
                <SubHeading>{t('appLandingPage.features.liveActivities.heading')}</SubHeading>
                <SubDescription>{t('appLandingPage.features.liveActivities.description')}</SubDescription>
            <BulletList>
                  {[0, 1, 2].map((index) => (
                <BulletItem key={index}>
                      <BulletDot color="blue" />
                  <BulletText>{t(`appLandingPage.features.liveActivities.bullets.${index}`)}</BulletText>
                </BulletItem>
              ))}
            </BulletList>
          </ContentColumn>
        </TwoColumnGrid>

        <TwoColumnGrid>
          <ImageColumn order={1}>
            <img
              src="/images/Applandingpage/Journeydetails_smallcrop.PNG"
              alt={t('appLandingPage.features.delayPredictions.imageAlt')}
              style={{
                borderRadius: '1.5rem',
                display: 'block',
                width: '100%',
                maxWidth: '30rem',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            />
          </ImageColumn>
          <ContentColumn order={2}>
                <SubHeading>{t('appLandingPage.features.delayPredictions.heading')}</SubHeading>
                <SubDescription>{t('appLandingPage.features.delayPredictions.description')}</SubDescription>
            <BulletList>
                  {[0, 1, 2].map((index) => (
                <BulletItem key={index}>
                      <BulletDot color="purple" />
                  <BulletText>{t(`appLandingPage.features.delayPredictions.bullets.${index}`)}</BulletText>
                </BulletItem>
              ))}
            </BulletList>
          </ContentColumn>
        </TwoColumnGrid>

        <TwoColumnGrid>
          <ContentColumn>
            <SubHeading>{t('appLandingPage.features.reliability.heading')}</SubHeading>
            <SubDescription>{t('appLandingPage.features.reliability.description')}</SubDescription>
            <BulletList>
              {[0, 1, 2].map((index) => (
                <BulletItem key={index}>
                      <BulletDot color="green" />
                  <BulletText>{t(`appLandingPage.features.reliability.bullets.${index}`)}</BulletText>
                </BulletItem>
              ))}
            </BulletList>
          </ContentColumn>
          <ImageColumn>
            <img
              src={getLocalizedReliabilityScoreImagePath(i18n.language)}
              alt={t('appLandingPage.features.reliability.imageAlt')}
                  style={{ borderRadius: '1.1rem' }}
            />
          </ImageColumn>
        </TwoColumnGrid>
          </>
        )}
      </Container>
    </FeaturesSection>
  );
}

